import { faseById } from '../game/campaign';
import {
  cleanName,
  localBest,
  playerName,
  setPlayerName,
  submitRun,
  topRuns,
  type BoardRow,
  type BoardScope,
  type RunResult,
} from '../net/leaderboard';
import { answerSurvey, markSurveyShown, surveyDue } from '../net/telemetry';

export interface SiteHooks {
  seed: () => string;
  onPlay: () => void;
  onCalibrate: () => void;
  onHow: () => void;
}

export interface Site {
  blocked: () => boolean;
  startRun: () => void;
  offerRun: (run: RunResult) => void;
  openBoard: (scope?: BoardScope) => void;
}

const el = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`missing #${id}`);
  return node as T;
};

const fmt = (value: number): string => value.toLocaleString('pt-BR');

const escape = (text: string): string => text.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

function seedLabel(seed: string): string {
  const match = /^solta-(\d{4})(\d{2})(\d{2})(?::(?:n(\d+)|([a-z0-9-]+)))?$/.exec(seed);
  if (match === null) return seed;
  const day = `${match[3]}/${match[2]}`;
  if (match[4] !== undefined) return `${day} · nível ${match[4]}`;
  const fase = faseById(match[5]);
  return fase === null ? day : `${day} · ${fase.worldName} F${fase.fase}`;
}

export function createSite(hooks: SiteHooks): Site {
  const topSeed = el('top-seed');
  const topName = el<HTMLButtonElement>('top-name');
  const footState = el('foot-state');
  const board = el('board');
  const boardRows = el<HTMLOListElement>('board-rows');
  const boardNote = el('board-note');
  const save = el('save');
  const saveForm = el<HTMLFormElement>('save-form');
  const saveName = el<HTMLInputElement>('save-name');
  const saveState = el('save-state');
  const saveSkip = el<HTMLButtonElement>('save-skip');
  const survey = el('survey');

  let scope: BoardScope = 'day';
  let pending: RunResult | null = null;
  let loading = 0;

  const syncName = (): void => {
    const name = playerName();
    topName.textContent = name ?? 'definir nome';
    topName.classList.toggle('named', name !== null);
  };

  const syncBest = (): void => {
    topSeed.textContent = `seed ${seedLabel(hooks.seed())}`;
    const best = localBest(hooks.seed());
    footState.textContent = best > 0 ? `seu recorde de hoje · ${fmt(best)}` : 'ranking global · hoje e geral';
  };

  const renderRows = (rows: readonly BoardRow[]): void => {
    boardRows.innerHTML = rows
      .map((row) => {
        const medal = row.rank <= 3 ? ` top${row.rank}` : '';
        return `<li class="row${medal}${row.you ? ' you' : ''}">
          <b>${row.rank}</b>
          <span class="who">${escape(row.name)}</span>
          <span class="pts">${fmt(row.score)}</span>
          <span class="acc">${(row.accuracy * 100).toFixed(0)}% · ×${row.multiplier}</span>
        </li>`;
      })
      .join('');
    if (rows.length === 0) boardRows.innerHTML = '<li class="empty">ninguém soltou no ápice ainda — seja o primeiro</li>';
  };

  const load = (): void => {
    const ticket = ++loading;
    boardNote.textContent = 'carregando…';
    void topRuns(scope, hooks.seed(), 20).then((page) => {
      if (ticket !== loading) return;
      renderRows(page.rows);
      boardNote.textContent = page.offline
        ? 'servidor fora do ar — mostrando só as suas corridas salvas neste navegador'
        : scope === 'day'
          ? `top 20 do seed de hoje (${seedLabel(hooks.seed())}) · o seed vira 00h UTC`
          : 'top 20 de sempre · melhor corrida de cada jogador';
    });
  };

  const openBoard = (next: BoardScope = scope): void => {
    scope = next;
    board.hidden = false;
    for (const tab of board.querySelectorAll<HTMLButtonElement>('[data-scope]')) {
      tab.classList.toggle('on', tab.dataset.scope === scope);
    }
    load();
  };

  const closeBoard = (): void => {
    board.hidden = true;
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  };

  const closeBoardIfOpen = (): void => {
    if (!board.hidden) closeBoard();
  };

  const syncSkip = (): void => {
    saveSkip.textContent = saveForm.hidden ? 'fechar' : 'agora não';
  };

  const askName = (): void => {
    save.hidden = false;
    saveForm.hidden = false;
    syncSkip();
    if (pending === null) saveState.textContent = 'o nome aparece no ranking global';
    saveName.value = playerName() ?? '';
    saveName.focus();
    saveName.select();
  };

  const send = (run: RunResult, name: string): void => {
    saveState.textContent = 'enviando…';
    void submitRun(run, name).then((result) => {
      syncName();
      syncBest();
      saveState.textContent =
        result.source === 'global'
          ? `no ar! ${result.rank === null ? '' : `você é #${result.rank} hoje · `}recorde ${fmt(result.best)}`
          : `sem conexão com o ranking — guardado neste navegador (recorde ${fmt(result.best)})`;
      if (!board.hidden) load();
    });
  };

  const offerRun = (run: RunResult): void => {
    pending = run;
    save.hidden = false;
    survey.hidden = !surveyDue();
    if (!survey.hidden) markSurveyShown();
    const name = playerName();
    if (name === null) {
      saveState.textContent = `${fmt(run.score)} pts — põe um nome pra entrar no ranking global`;
      saveName.value = '';
      saveForm.hidden = false;
      if (!(typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches)) saveName.focus();
    } else {
      saveForm.hidden = true;
      send(run, name);
    }
    syncSkip();
  };

  const startRun = (): void => {
    syncBest();
    pending = null;
    save.hidden = true;
    survey.hidden = true;
    saveForm.hidden = false;
    closeBoardIfOpen();
  };

  saveForm.addEventListener('submit', (event) => {
    event.preventDefault();
    const name = cleanName(saveName.value) || 'anônimo';
    setPlayerName(name);
    syncName();
    saveForm.hidden = true;
    syncSkip();
    if (pending === null) saveState.textContent = `beleza, ${name} — jogue uma corrida pra entrar no ranking`;
    else send(pending, name);
    saveName.blur();
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const tab = target.closest<HTMLElement>('[data-scope]');
    if (tab !== null) {
      openBoard(tab.dataset.scope === 'all' ? 'all' : 'day');
      tab.blur();
      return;
    }

    const action = target.closest<HTMLElement>('[data-act]');
    if (action === null) {
      if (!board.hidden && target === board) closeBoard();
      return;
    }

    switch (action.dataset.act) {
      case 'play':
        closeBoardIfOpen();
        save.hidden = true;
        hooks.onPlay();
        break;
      case 'again-yes':
      case 'again-no':
        answerSurvey(action.dataset.act === 'again-yes');
        survey.textContent = 'valeu!';
        break;
      case 'board':
        openBoard();
        break;
      case 'how':
        closeBoardIfOpen();
        hooks.onHow();
        break;
      case 'calib':
        closeBoardIfOpen();
        hooks.onCalibrate();
        break;
      case 'name':
        askName();
        break;
      case 'skip':
        save.hidden = true;
        break;
      case 'close':
        closeBoard();
        break;
      default:
        break;
    }
    action.blur();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeBoardIfOpen();
      save.hidden = true;
      saveName.blur();
    }
  });

  const blocked = (): boolean => {
    if (!board.hidden) return true;
    const focused = document.activeElement;
    return focused instanceof HTMLElement && focused.closest('[data-ui]') !== null;
  };

  syncName();
  syncBest();

  return { blocked, startRun, offerRun, openBoard };
}
